import { drawApprovedAmount, drawCommittedAmount, drawWithheldAmount, effectiveDrawInvoices } from "../financial.ts";
import type { AppData } from "@/lib/types";

export type AquaView = "dashboard" | "clients" | "client" | "job" | "sow" | "actions" | "invoices" | "settings" | "diagnostics";
export type AquaStatus = "standby" | "connecting" | "listening" | "thinking" | "speaking" | "error";
export type AquaControlConsequence = "safe" | "state_change" | "financial" | "destructive" | "external";

export type AquaVisibleControl = {
  id: string;
  label: string;
  kind: "button" | "link" | "tab" | "form";
  consequence: AquaControlConsequence;
  teachingReason?: string;
  expectedResult?: string;
};
export type AquaWritableField = { id: string; label: string; inputType: string; required: boolean };

export type AquaScreenContext = {
  appId: "aqua_draw";
  appName: "AquaDraw";
  integrationVersion: "0.22.6-guided-invoice-payments";
  view: AquaView;
  portalMode: boolean;
  selectedClientId: string | null;
  selectedJobId: string | null;
  selectedSowId: string | null;
  selectedInvoiceRequestId: string | null;
  financial: {
    originalBudget: number;
    approvedOverages: number;
    actualBudget: number;
    contractValue: number;
    requested: number;
    approved: number;
    withheld: number;
    received: number;
    available: number;
  };
  registeredControls: string[];
  visibleControls: AquaVisibleControl[];
  writableFields: AquaWritableField[];
  viewport: { scrollY: number; maxScrollY: number; atTop: boolean; atBottom: boolean; focusedFieldId: string | null };
  records: {
    clients: Array<{ id: string; name: string; company?: string; gcFeePercent?: number }>;
    jobs: Array<{ id: string; clientId: string; title: string; address: string }>;
    sowItems: Array<{ id: string; jobId: string; title: string; description: string; contractAmount: number; approvedBudgetChanges: number; approvedChangeReason: string; adjustedBudget: number }>;
    draws: Array<{ id: string; sowId: string; requestId: string; requestNumber?: string; amount: number; approvedAmount: number; withheldAmount: number; holdbackPercent: number; holdbackReason: string; explanation: string; status: string; createdAt: string; estimatedArrivalDate?: string; receivedAt?: string; photoCount: number }>;
    invoices: Array<{ requestId: string; requestNumber: string; clientId: string; jobId: string; requestedSubtotal: number; gcFeeAmount: number; requestedTotal: number; approvedTotal: number; withheldTotal: number; holdbackPercent: number; holdbackReason: string; status: string; remainingContractBalance: number; createdAt: string }>;
  };
};

export type AquaActionReceipt = { ok: boolean; action: string; target: string; priorView: AquaView; resultingView: AquaView; verified: boolean; message: string; timestamp: string };

export type AquaOperator = {
  getContext(): AquaScreenContext;
  navigate(view: Extract<AquaView, "dashboard" | "clients" | "actions" | "invoices" | "settings" | "diagnostics">): void;
  openClient(id: string): void;
  openJob(id: string): void;
  openSow(id: string): void;
  openInvoice(requestId: string): void;
  openRequestReview(requestId: string): void;
  highlight(controlId: string): boolean;
  history(direction: "back" | "forward"): boolean;
  scroll(direction: "up" | "down" | "top" | "bottom"): boolean;
  activate(controlId: string): Promise<boolean>;
  fillField(fieldId: string, value: string): boolean;
  pressEnter(fieldId?: string): Promise<boolean>;
  confirm(summary: string): Promise<boolean>;
  createClientAndProject(input: { name: string; company?: string; email: string; phone: string; address: string; title: string }): { clientId: string; jobId: string };
  createJob(input: { clientId: string; address: string; title: string }): { jobId: string };
  createSow(input: { jobId: string; title: string; description: string; contractAmount: number; approvedBudgetChanges: number; approvedChangeReason: string }): { sowId: string };
  createDraw(input: { sowId: string; amount: number; explanation: string }): { drawId: string };
  receivePayment(id: string): void;
  deleteRecord(input: { recordType: "client" | "job" | "sow" | "draw"; id: string }): void;
};

export type AquaTurn = { id: string; role: "user" | "assistant" | "system"; text: string; timestamp: string };
export type AquaDiagnostic = { timestamp: string; event: string; detail: string; status: "PASS" | "FAIL" | "INFO" | "LIMITATION" };

export function buildScreenContext(input: {
  data: AppData;
  view: AquaView;
  portalMode: boolean;
  clientId?: string | null;
  jobId?: string | null;
  sowId?: string | null;
  requestId?: string | null;
  visibleControls?: AquaVisibleControl[];
  writableFields?: AquaWritableField[];
  viewport?: AquaScreenContext["viewport"];
}): AquaScreenContext {
  const originalBudget = input.data.sowItems.reduce((sum, item) => sum + item.contractAmount, 0);
  const approvedOverages = input.data.sowItems.reduce((sum, item) => sum + (item.approvedBudgetChanges ?? 0), 0);
  const contractValue = originalBudget + approvedOverages;
  const requested = input.data.draws.reduce((sum, draw) => sum + drawCommittedAmount(draw), 0);
  const approved = input.data.draws.reduce((sum, draw) => sum + drawApprovedAmount(draw), 0);
  const withheld = input.data.draws.reduce((sum, draw) => sum + drawWithheldAmount(draw), 0);
  const received = input.data.draws.filter((draw) => draw.status === "RECEIVED").reduce((sum, draw) => sum + drawApprovedAmount(draw), 0);

  return {
    appId: "aqua_draw",
    appName: "AquaDraw",
    integrationVersion: "0.22.6-guided-invoice-payments",
    view: input.view,
    portalMode: input.portalMode,
    selectedClientId: input.clientId ?? null,
    selectedJobId: input.jobId ?? null,
    selectedSowId: input.sowId ?? null,
    selectedInvoiceRequestId: input.requestId ?? null,
    financial: { originalBudget, approvedOverages, actualBudget: contractValue, contractValue, requested, approved, withheld, received, available: contractValue - requested },
    registeredControls: ["nav-dashboard", "nav-clients", "nav-jobs", "nav-sow", "nav-draws", "nav-invoices", "nav-settings", "nav-diagnostics", "new-client", "export-client-invoices", "export-client-requests-by-date", "ask-aqua"],
    visibleControls: input.visibleControls ?? [],
    writableFields: input.writableFields ?? [],
    viewport: input.viewport ?? { scrollY: 0, maxScrollY: 0, atTop: true, atBottom: true, focusedFieldId: null },
    records: {
      clients: input.data.clients.map(({ id, name, company, gcFeePercent }) => input.portalMode ? ({ id, name, company }) : ({ id, name, company, gcFeePercent: gcFeePercent ?? 0 })),
      jobs: input.data.jobs.map(({ id, clientId, title, address }) => ({ id, clientId, title, address })),
      sowItems: input.data.sowItems.map(({ id, jobId, title, description, contractAmount, approvedBudgetChanges, approvedChangeReason }) => ({ id, jobId, title, description, contractAmount, approvedBudgetChanges: approvedBudgetChanges ?? 0, approvedChangeReason: approvedChangeReason ?? "", adjustedBudget: contractAmount + (approvedBudgetChanges ?? 0) })),
      draws: input.data.draws.map((draw) => ({ id: draw.id, sowId: draw.sowId, requestId: draw.requestId || draw.id, requestNumber: draw.requestNumber, amount: draw.amount, approvedAmount: drawApprovedAmount(draw), withheldAmount: drawWithheldAmount(draw), holdbackPercent: draw.holdbackPercent || 0, holdbackReason: draw.holdbackReason || "", explanation: draw.explanation, status: draw.status, createdAt: draw.createdAt, estimatedArrivalDate: draw.estimatedArrivalDate, receivedAt: draw.receivedAt, photoCount: Math.max(draw.mediaNames?.length ?? 0, draw.photoDataUrls?.length ?? 0) })),
      invoices: effectiveDrawInvoices(input.data).map((invoice) => ({ requestId: invoice.requestId, requestNumber: invoice.requestNumber, clientId: invoice.clientId, jobId: invoice.jobId, requestedSubtotal: invoice.requestedSubtotal, gcFeeAmount: invoice.gcFeeAmount, requestedTotal: invoice.requestedTotal, approvedTotal: invoice.approvedTotal, withheldTotal: invoice.withheldTotal, holdbackPercent: invoice.holdbackPercent, holdbackReason: invoice.holdbackReason, status: invoice.status, remainingContractBalance: invoice.remainingContractBalance, createdAt: invoice.createdAt })),
    },
  };
}
